// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import {
  fetchPurchasesCertificatesDownload,
  getPurchasesTracesDetail,
} from 'apis/purchases/purchaseApi';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import PurchaseTraceBoard from 'components/organisms/PurchaseTraceBoard';
import PurchaseTemplate from 'components/templates/PurchaseTemplate';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Path } from 'routes';
import { TraceDataDetail } from 'types/purchase';
import styles from './PurchaseTraceDataDetail.module.scss';
import { OperatorType, PurchaseTraceStatus } from 'util/Enums';

const PurchaseTraceDataDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const materialNameFromUrl = new URLSearchParams(window.location.search).get(
    'materialName'
  );
  const [traceDetail, setTraceDetail] = useState<TraceDataDetail[]>([]);

  const hasBeenProcured = useMemo(
    () =>
      traceDetail[traceDetail.length - 1]?.status ===
      PurchaseTraceStatus.Procured,
    [traceDetail]
  );

  const name = useMemo(() => {
    if (hasBeenProcured) {
      return id;
    }

    const materialFoundInTrace = traceDetail
      .filter(({ stage }) => stage !== OperatorType.Emissions)
      .map(({ sites }) => sites)
      .flat()
      .map((e) => e.data)
      .flat()
      .find(({ description }) => !!description)?.description;

    if (materialFoundInTrace) {
      return materialFoundInTrace;
    }

    if (materialNameFromUrl) {
      return materialNameFromUrl;
    }

    return id;
  }, [id, hasBeenProcured, traceDetail, materialNameFromUrl]);

  const title = useMemo(() => {
    return `${name} ${t('purchase_page.trace_data_detail.title')}`;
  }, [name, t]);

  useEffect(() => {
    fetchPurchasesTracesDetail();
  }, []);

  const fetchPurchasesTracesDetail = () => {
    getPurchasesTracesDetail(id, (data) => {
      data && setTraceDetail(data);
    });
  };

  const handleExportCertificate = () => {
    fetchPurchasesCertificatesDownload(id, (data) => {
      const downloadEl = document.createElement('a');
      downloadEl.href = `data:application/pdf;base64, ${data.pdfBase64}`;
      downloadEl.download = data.fileName;
      downloadEl.click();
      downloadEl.remove();
    });
  };

  return (
    <PurchaseTemplate
      breadcrumb={[
        { href: Path.purchaseTraces, label: t('purchase_nav.traceability') },
        { label: name },
      ]}
    >
      <div className={styles.traceDataDetail}>
        <div className={styles.header}>
          <h1 className={styles.heading}>{title}</h1>

          {hasBeenProcured && (
            <div className={styles.actions}>
              <button
                className={styles.downloadCertificate}
                onClick={() => handleExportCertificate()}
              >
                <IconDownload />
                {t('purchase_page.trace_data_detail.certificate_download')}
              </button>
            </div>
          )}
        </div>

        <div className={styles.wrapper}>
          <PurchaseTraceBoard traceDetail={traceDetail} />
        </div>
      </div>
    </PurchaseTemplate>
  );
};

export default PurchaseTraceDataDetailPage;
